import { useState } from "react";
import { AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { btnPrimary, btnGhost, inputCls } from "./shared";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export function EditCampaignDialog({ campaign, onClose, onSaved }) {
  const [navn, setNavn] = useState(campaign.navn);
  const [tittel, setTittel] = useState(campaign.tittel);
  const [verdi, setVerdi] = useState(campaign.verdi_kr ?? 0);
  const [utloper, setUtloper] = useState(toLocalInput(campaign.utloper));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/campaigns/${campaign.id}`, {
        navn: navn.trim(),
        tittel: tittel.trim(),
        verdi_kr: Number(verdi) || 0,
        utloper: utloper ? new Date(utloper).toISOString() : null,
      });
      toast.success("Kampanjen er oppdatert");
      onSaved(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#282421] text-[#FDFBF7] sm:max-w-lg" data-testid="edit-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Rediger kampanje</DialogTitle>
          <DialogDescription className="text-[#A8A29E]">{campaign.gruppe_navn} · sendt til {campaign.antall_mottakere} kunder</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200" data-testid="edit-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Kampanjen er allerede sendt. Kundene får <strong>ikke</strong> en ny melding med oppdatert innhold – endringene er kun synlige
            internt og på kupong-lenken kundene allerede har mottatt.
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Belønning / kampanjetekst</span>
            <input value={tittel} onChange={(e) => setTittel(e.target.value)} data-testid="edit-title-input" className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Kampanjenavn (internt)</span>
            <input value={navn} onChange={(e) => setNavn(e.target.value)} data-testid="edit-name-input" className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Verdi per innløsning (kr)</span>
            <input type="number" min="0" value={verdi} onChange={(e) => setVerdi(e.target.value)} data-testid="edit-value-input" className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs uppercase tracking-wide text-[#A8A29E]">Utløper</span>
            <input type="datetime-local" value={utloper} onChange={(e) => setUtloper(e.target.value)} data-testid="edit-expiry-input" className={`${inputCls} [color-scheme:dark]`} />
          </label>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onClose} className={btnGhost} data-testid="edit-cancel-btn">Avbryt</button>
          <button onClick={save} disabled={saving || tittel.trim().length < 3 || navn.trim().length < 3} className={btnPrimary} data-testid="edit-save-btn">
            <Save className="h-4 w-4" /> {saving ? "Lagrer…" : "Lagre endringer"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
